import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { API_BASE_URL } from "../core/api-base-url";

import { ISkillNode, IAddSkillNodeRequest } from "../models/skillNode";
import ISkillType from "../models/skillType";
import ISkillAlias from "../models/skillAlias";

import IUserSkillResponse from "../models/userSkillsResponse";
import IUser from "../models/user";
import IUpsertUserSkills from "../models/upsertUserSkills";
import IUpdateUserSkills from "../models/updateUserSkills";
import {
  IDeleteUserSkill,
  IDeleteUserSkills,
} from "@skills-app/models/deleteUserSkill";
import ISkillNodeWithRating from "@skills-app/models/skillNodeWithRating";
import IOrgNode from "@skills-app/models/orgNode";

type DeleteResult = boolean | { successful?: boolean } | null | undefined;

export interface IVerifySkillNameResponse {
  similarSkills: string[];
}

@Injectable({ providedIn: "root" })
export class SkillsService {
  /**
   * API_BASE_URL is "/api" in this repo.
   * So endpoints must be built as `${baseUrl}/Controller/Action`
   * (NOT `${baseUrl}api/...` which duplicates the /api prefix).
   */
  private readonly baseUrl = API_BASE_URL;

  private readonly options = {
    headers: new HttpHeaders({ "Content-Type": "application/json" }),
  };

  constructor(private readonly http: HttpClient) {}

  public getAllSkillTypes(): Observable<ISkillType[]> {
    return this.http.get<ISkillType[]>(
      `${this.baseUrl}/SkillTypes`,
      this.options,
    );
  }

  public addSkillType(name: string): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/SkillTypes`,
      { Name: name },
      this.options,
    );
  }

  public updateSkillType(skillType: ISkillType): Observable<unknown> {
    const id = skillType.Id;
    return this.http.put(
      `${this.baseUrl}/SkillTypes/${id}`,
      skillType,
      this.options,
    );
  }

  public deleteSkillTypeById(id: string): Observable<unknown> {
    return this.http.delete(`${this.baseUrl}/SkillTypes/${id}`, this.options);
  }

  public getAllSkillNodes(): Observable<ISkillNode[]> {
    return this.http.get<ISkillNode[]>(
      `${this.baseUrl}/SkillNodes`,
      this.options,
    );
  }

  public getAllSkillNodesFilteredByRelatedSkillsThreshold(
    threshold: number,
  ): Observable<ISkillNode[]> {
    return this.http.get<ISkillNode[]>(
      `${this.baseUrl}/SkillNodes/UnderThresholdRelatedSkills/${threshold}`,
      this.options,
    );
  }

  public getAllSkillNodesForSearch(id: string): Observable<ISkillNode[]> {
    return this.http.get<ISkillNode[]>(
      `${this.baseUrl}/SkillNodes/Search?id=${encodeURIComponent(id)}`,
      this.options,
    );
  }

  // Typed so callers can safely read result.similarSkills (string[])
  public verifySkillNameDoesNotExist(
    skillName: string,
  ): Observable<IVerifySkillNameResponse> {
    return this.http.get<IVerifySkillNameResponse>(
      `${this.baseUrl}/SkillNodes/VerifySkillDoesNotExist/false?skillName=${encodeURIComponent(
        skillName,
      )}`,
      this.options,
    );
  }

  public updateSkillNode(skillNode: ISkillNode): Observable<unknown> {
    const id = skillNode.Id;
    return this.http.put(
      `${this.baseUrl}/SkillNodes/${id}`,
      skillNode,
      this.options,
    );
  }

  public forceUpdateSkillNode(skillNode: ISkillNode): Observable<unknown> {
    const id = skillNode.Id;
    return this.http.put(
      `${this.baseUrl}/SkillNodes/Force/${id}`,
      skillNode,
      this.options,
    );
  }

  public addSkillNode(skillNode: IAddSkillNodeRequest): Observable<ISkillNode> {
    return this.http.post<ISkillNode>(
      `${this.baseUrl}/SkillNodes`,
      skillNode,
      this.options,
    );
  }

  public forceAddSkillNode(
    skillNode: IAddSkillNodeRequest,
  ): Observable<ISkillNode> {
    return this.http.post<ISkillNode>(
      `${this.baseUrl}/SkillNodes/Force`,
      skillNode,
      this.options,
    );
  }

  public deleteSkillNodeById(id: string): Observable<boolean> {
    return this.http
      .delete<DeleteResult>(
        `${this.baseUrl}/SkillNodes/${encodeURIComponent(id)}`,
        this.options,
      )
      .pipe(
        map((r) => {
          if (r === true) return true;
          if (r === false || r == null) return false;

          if (typeof r === "object" && "successful" in r) {
            const obj = r as { successful?: boolean };
            return obj.successful === true;
          }

          return false;
        }),
      );
  }

  public getRelatedSkillNodesBySkillNodeId(
    id: string,
  ): Observable<ISkillNode[]> {
    return this.http.get<ISkillNode[]>(
      `${this.baseUrl}/SkillEdges/${encodeURIComponent(id)}`,
      this.options,
    );
  }

  public removeSkillEdge(
    id: string,
    otherId: string,
  ): Observable<ISkillNode[]> {
    return this.http.delete<ISkillNode[]>(
      `${this.baseUrl}/SkillEdges?skillNodeId=${encodeURIComponent(
        id,
      )}&otherId=${encodeURIComponent(otherId)}`,
      this.options,
    );
  }

  public linkToSkillGroup(
    id: string,
    skillGroupId: string,
  ): Observable<unknown> {
    return this.http.get(
      `${this.baseUrl}/SkillNodes/LinkToSkillGroup/${encodeURIComponent(
        id,
      )}/${encodeURIComponent(skillGroupId)}`,
      this.options,
    );
  }

  public deleteLinkToSkillGroup(
    id: string,
    skillGroupId: string,
  ): Observable<unknown> {
    return this.http.delete(
      `${this.baseUrl}/SkillNodes/RemoveLinkToSkillGroup/${encodeURIComponent(
        id,
      )}/${encodeURIComponent(skillGroupId)}`,
      this.options,
    );
  }

  public addSkillEdge(skill1Id: string, skill2Id: string): Observable<unknown> {
    const payload = { Skill1Id: skill1Id, Skill2Id: skill2Id };
    return this.http.post(`${this.baseUrl}/SkillEdges`, payload, this.options);
  }

  public addSkillAlias(
    parentId: string,
    childId: string[],
  ): Observable<unknown> {
    const payload = {
      ParentSkillNodeId: parentId,
      ChildSkillNodeIds: childId,
    };
    return this.http.post(`${this.baseUrl}/SkillAlias`, payload, this.options);
  }

  public getAllSkillAliases(): Observable<ISkillAlias[]> {
    return this.http.get<ISkillAlias[]>(
      `${this.baseUrl}/SkillAlias`,
      this.options,
    );
  }

  public deleteSkillAlias(
    parentId: string,
    childId: string,
  ): Observable<unknown> {
    return this.http.delete(
      `${this.baseUrl}/SkillAlias?parentSkillNodeId=${encodeURIComponent(
        parentId,
      )}&childSkillNodeId=${encodeURIComponent(childId)}`,
      this.options,
    );
  }

  public getChildSkills(parentId: string): Observable<ISkillNode[]> {
    return this.http.get<ISkillNode[]>(
      `${this.baseUrl}/SkillAlias/SkillAliasBySkillId?parentId=${encodeURIComponent(
        parentId,
      )}`,
      this.options,
    );
  }

  public getAllUserSkills(): Observable<IUserSkillResponse> {
    return this.http.get<IUserSkillResponse>(
      `${this.baseUrl}/UserSkills/GetAllUsers`,
      this.options,
    );
  }

  /**
   * Backend expects a JSON array body. {} causes 400.
   * Passing [] returns the full list.
   */
  public getSquadRoles(selectedSquads: string[] = []): Observable<string[]> {
    return this.http.post<string[]>(
      `${this.baseUrl}/UserSkills/Roles`,
      selectedSquads ?? [],
      this.options,
    );
  }

  public getAllOrgNodes(): Observable<IOrgNode[]> {
    return this.http.get<IOrgNode[]>(
      `${this.baseUrl}/Organisations/all`,
      this.options,
    );
  }

  /**
   * Backend expects a JSON array body. {} causes 400.
   * Passing [] returns the full list.
   */
  public getAllLocations(selectedSquads: string[] = []): Observable<string[]> {
    return this.http.post<string[]>(
      `${this.baseUrl}/UserSkills/Locations`,
      selectedSquads ?? [],
      this.options,
    );
  }

  public getAllSkillsCount(): Observable<number> {
    return this.http.get<number>(
      `${this.baseUrl}/SkillNodes/Count`,
      this.options,
    );
  }

  public getAllUserCount(): Observable<number> {
    return this.http.get<number>(
      `${this.baseUrl}/UserSkills/Count`,
      this.options,
    );
  }

  public getCurrentUser(): Observable<IUser> {
    return this.http.get<IUser>(
      `${this.baseUrl}/UserSkills/GetLoggedInUserByEmail`,
      this.options,
    );
  }

  public GetCurrentUserSkills(): Observable<ISkillNodeWithRating[]> {
    return this.http.get<ISkillNodeWithRating[]>(
      `${this.baseUrl}/UserSkills/GetCurrentUserSkills`,
      this.options,
    );
  }

  public GetGivenUserSkills(
    azureId: string,
  ): Observable<ISkillNodeWithRating[]> {
    return this.http.get<ISkillNodeWithRating[]>(
      `${this.baseUrl}/UserSkills/GetUserSkillsById/${encodeURIComponent(azureId)}`,
      this.options,
    );
  }

  public currentUserHasSkills(): Observable<boolean> {
    return this.http.get<boolean>(
      `${this.baseUrl}/UserSkills/CurrentUserHasSkills/`,
      this.options,
    );
  }

  public triggerAzureSync(): Observable<unknown> {
    return this.http.get(
      `${this.baseUrl}/Sync/TriggerAzureSyncByAdmin`,
      this.options,
    );
  }

  public upsertUserSkills(
    upsertUserSkillsModel: IUpsertUserSkills,
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/UserSkills/UpsertUserSkills`,
      upsertUserSkillsModel,
      this.options,
    );
  }

  public updateUserSkill(
    updateUserSkillModel: IUpdateUserSkills,
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/UserSkills/UpdateUserSkill`,
      updateUserSkillModel,
      this.options,
    );
  }

  public deleteUserSkill(
    deleteUserSkillModel: IDeleteUserSkill,
  ): Observable<unknown> {
    const params = {
      azureId: deleteUserSkillModel.AzureId,
      skillId: deleteUserSkillModel.SkillId,
    };
    return this.http.delete(`${this.baseUrl}/UserSkills/DeleteUserSkill`, {
      ...this.options,
      params,
    });
  }

  public deleteMultipleUserSkills(
    deleteUserSkillModel: IDeleteUserSkills,
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/UserSkills/BulkDeleteUserSkills`,
      deleteUserSkillModel,
      this.options,
    );
  }

  public updateUserSkillsOrder(
    azureId: string,
    skills: ISkillNodeWithRating[],
  ): Observable<unknown> {
    return this.http.post(
      `${this.baseUrl}/UserSkills/UpdateUserSkillsOrder`,
      { azureId, skills },
      this.options,
    );
  }
}
